import type { BookingGarment } from '../api/bookings';

/**
 * Which master a tailor is on, and their key there.
 *
 * <p>
 * <strong>Null is a real answer and the commonest one at a busy counter.</strong> A garment goes to
 * whoever can take it that afternoon, including somebody nobody has set up, so the name on the
 * garment is always filled and the link is filled when there is one.
 * </p>
 */
export type TailorRef =
  { master: 'vendor'; id: number } | { master: 'employee'; id: number } | null;

/**
 * The two ids on a garment, read as one answer.
 *
 * <p>
 * They are never both set — a check constraint on `TblRentalOrderItem` enforces it — so the vendor
 * is tried first and the employee second rather than the two being merged. If a row ever arrives
 * with both, this reports the vendor and the *screen stays consistent with itself*, which is a
 * better failure than a control that silently changes which master it is writing to.
 * </p>
 */
export function tailorRefOf(garment: BookingGarment): TailorRef {
  if (garment.alterationTailorVendorId !== null) {
    return { master: 'vendor', id: garment.alterationTailorVendorId };
  }

  if (garment.alterationTailorEmployeeId !== null) {
    return { master: 'employee', id: garment.alterationTailorEmployeeId };
  }

  return null;
}

/**
 * One string that identifies a tailor across both masters.
 *
 * <p>
 * <strong>The prefix is load-bearing, not cosmetic.</strong> `TblVendor` and `TblEmployee` draw
 * keys from the same IDENTITY seed, so vendor 1000004 and employee 1000004 both exist and are
 * different people. Anything that groups, de-duplicates or compares tailors across the two — the
 * picker's option list, the print bar's "is every sheet going to the same person" test — is wrong
 * on a bare number, and wrong in the way nobody reports: it does not throw, it just answers about
 * somebody else. This is the same collision `NamesABooking` guards on the server.
 * </p>
 *
 * <p>
 * A typed name has no key, so it is `typed` — every typed tailor compares equal to every other,
 * which is right for the one question asked of it: none of them can have an answer remembered
 * against them.
 * </p>
 */
export function tailorKey(ref: TailorRef): string {
  if (ref === null) {
    return 'typed';
  }

  return ref.master === 'vendor' ? `v:${ref.id}` : `e:${ref.id}`;
}

/** The inverse, for a control that hands back one of its own keys. */
export function parseTailorKey(key: string): TailorRef {
  if (key.startsWith('v:')) {
    return { master: 'vendor', id: Number(key.slice(2)) };
  }

  if (key.startsWith('e:')) {
    return { master: 'employee', id: Number(key.slice(2)) };
  }

  return null;
}

/**
 * Where to write a remembered docket language.
 *
 * <p>
 * Two endpoints, because two masters — and it is derived here rather than at the print bar so the
 * bar cannot post an employee's key to `/api/vendor`, which would answer 404 for a tailor who
 * plainly exists.
 * </p>
 */
export function docketLanguagePath(ref: TailorRef): string | null {
  if (ref === null) {
    return null;
  }

  return ref.master === 'vendor'
    ? `/api/vendor/${ref.id}/docket-language`
    : `/api/employee/${ref.id}/docket-language`;
}
